<template>
  <div class="mail-request-container">
    <div class="header-section">      
      <!-- Search and Filter Bar -->
      <div class="search-filter-bar">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="$t('raMailRequest.search.placeholder')"
            @input="handleSearch"
          >
        </div>
        
        <div class="filter-group">
          <select v-model="dateFilter" @change="handleFilter">
            <option value="">{{ $t('raMailRequest.filters.dateRange.allTime') }}</option>
            <option value="today">{{ $t('raMailRequest.filters.dateRange.today') }}</option>
            <option value="week">{{ $t('raMailRequest.filters.dateRange.week') }}</option>
            <option value="month">{{ $t('raMailRequest.filters.dateRange.month') }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Main Tabs -->
    <div class="main-tabs-row">
      <div class="main-tabs">
        <button 
          :class="['main-tab-btn', { active: activeMainTab === 'requested' }]"
          @click="setMainTab('requested')"
        >
          {{ $t('raMailRequest.tabs.requested') }}
          <span class="count" v-if="getRequestedCount">{{ getRequestedCount }}</span>
        </button>
        <button 
          :class="['main-tab-btn', { active: activeMainTab === 'completed' }]"
          @click="setMainTab('completed')"
        >
          {{ $t('raMailRequest.tabs.completed') }}
          <span class="count" v-if="getCompletedCount">{{ getCompletedCount }}</span>
        </button>
      </div>
      <div class="view-toggle">
        <button :class="{active: viewMode === 'grid'}" @click="viewMode = 'grid'" :title="$t('raMailRequest.viewToggle.grid')">
          <i class="fas fa-th"></i>
        </button>
        <button :class="{active: viewMode === 'table'}" @click="viewMode = 'table'" :title="$t('raMailRequest.viewToggle.table')">
          <i class="fas fa-table"></i>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>{{ $t('raMailRequest.states.loading') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <button @click="fetchMailRequests" class="retry-btn">
        <i class="fas fa-redo"></i> {{ $t('raMailRequest.states.retry') }}
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredAndSortedMail.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p>{{ $t('raMailRequest.states.empty') }}</p>
      <button v-if="hasActiveFilters" @click="clearFilters" class="clear-filters-btn">
        {{ $t('raMailRequest.states.clearFilters') }}
      </button>
    </div>

    <!-- Grid View -->
    <div v-else-if="viewMode === 'grid'" class="mail-grid-section">
      <div class="mail-grid">
        <div v-for="mail in filteredAndSortedMail" :key="mail.id" class="mail-card">
          <div class="mail-card-header">
            <span class="mail-id">#{{ formatMailId(mail.receivedDate) }}</span>
            <span :class="['status-badge', mail.status]">{{ $t(`raMailRequest.status.${mail.status.toLowerCase()}`) }}</span>
          </div>
          <div class="mail-card-body">
            <div class="mail-info-row">
              <strong>{{ $t('raMailRequest.table.business') }}:</strong> {{ mail.business?.legalBusinessName || 'N/A' }}
            </div>
            <div class="mail-info-row">
              <strong>{{ $t('raMailRequest.table.type') }}:</strong> {{ mail.mailType }}
            </div>
            <div class="mail-info-row">
              <strong>{{ $t('raMailRequest.table.receivedDate') }}:</strong> {{ formatDate(mail.receivedDate) }}
            </div>
            <div v-if="mail.description" class="mail-info-row">
              <strong>{{ $t('raMailRequest.table.description') }}:</strong> {{ mail.description }}
            </div>
          </div>
          <div class="mail-card-actions">
            <button
              v-if="mail.status === 'Scan_requested'"
              class="action-btn scan"
              @click="openScanUpload(mail.id)"
            >{{ $t('raMailRequest.actions.scan') }}</button>
            <button
              v-else-if="mail.status === 'Forward_requested'"
              class="action-btn forward"
              @click="handleForward(mail)"
            >{{ $t('raMailRequest.actions.forward') }}</button>
            <button
              v-else-if="mail.status === 'Shred_requested'"
              class="action-btn shred"
              @click="handleShred(mail)"
            >{{ $t('raMailRequest.actions.shred') }}</button>
            <button
              v-else-if="['Scanned', 'Forwarded', 'Shredded'].includes(mail.status)"
              class="action-btn preview"
              @click="viewMailPreview(mail)"
            ><i class="fas fa-eye"></i> {{ $t('raMailRequest.actions.view') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table View -->
    <table v-else class="mail-table">
      <thead>
        <tr>
          <th>{{ $t('raMailRequest.table.id') }}</th>
          <th>{{ $t('raMailRequest.table.status') }}</th>
          <th>{{ $t('raMailRequest.table.receivedDate') }}</th>
          <th>{{ $t('raMailRequest.table.business') }}</th>
          <th>{{ $t('raMailRequest.table.type') }}</th>
          <th>{{ $t('raMailRequest.table.description') }}</th>
          <th>{{ $t('raMailRequest.table.action') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="mail in filteredAndSortedMail" :key="mail.id">
          <td>#{{ formatMailId(mail.receivedDate) }}</td>
          <td><span :class="['status-badge', mail.status]">{{ $t(`raMailRequest.status.${mail.status.toLowerCase()}`) }}</span></td>
          <td>{{ formatDate(mail.receivedDate) }}</td>
          <td>{{ mail.business?.legalBusinessName || 'N/A' }}</td>
          <td>{{ mail.mailType }}</td>
          <td>{{ mail.description }}</td>
          <td>
            <button
              v-if="mail.status === 'Scan_requested'"
              class="action-btn scan"
              @click="openScanUpload(mail.id)"
            >{{ $t('raMailRequest.actions.scan') }}</button>
            <button
              v-else-if="mail.status === 'Forward_requested'"
              class="action-btn forward"
              @click="handleForward(mail)"
            >{{ $t('raMailRequest.actions.forward') }}</button>
            <button
              v-else-if="mail.status === 'Shred_requested'"
              class="action-btn shred"
              @click="handleShred(mail)"
            >{{ $t('raMailRequest.actions.shred') }}</button>
            <button
              v-else-if="['Scanned', 'Forwarded', 'Shredded'].includes(mail.status)"
              class="action-btn preview"
              @click="viewMailPreview(mail)"
            ><i class="fas fa-eye"></i> {{ $t('raMailRequest.actions.view') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mobile Filter Drawer -->
    <div class="mobile-filter-drawer" :class="{ open: showMobileFilters }">
      <div class="drawer-header">
        <h3>{{ $t('raMailRequest.mobile.filters') }}</h3>
        <button @click="showMobileFilters = false">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="drawer-content">
        <div class="filter-section">
          <label>{{ $t('raMailRequest.filters.dateRange.label') }}</label>
          <select v-model="dateFilter" @change="handleFilter">
            <option value="">{{ $t('raMailRequest.filters.dateRange.allTime') }}</option>
            <option value="today">{{ $t('raMailRequest.filters.dateRange.today') }}</option>
            <option value="week">{{ $t('raMailRequest.filters.dateRange.week') }}</option>
            <option value="month">{{ $t('raMailRequest.filters.dateRange.month') }}</option>
          </select>
        </div>
      </div>
      <div class="drawer-footer">
        <button class="clear-btn" @click="clearFilters">{{ $t('raMailRequest.mobile.clear') }}</button>
        <button class="apply-btn" @click="showMobileFilters = false">{{ $t('raMailRequest.mobile.apply') }}</button>
      </div>
    </div>

    <!-- Scan Upload Modal -->
    <ScanUploadModal 
      :show="showScanModal"
      :mailId="selectedMailId"
      @close="closeScanModal"
      @upload-success="handleScanSuccess"
    />

    <!-- Preview Modal -->
    <PreviewModal
      v-if="showPreviewModal"
      :mail="selectedMail"
      @close="closePreviewModal"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import incomingMailService from '../services/incomingMailService';
import ScanUploadModal from '../components/ScanUploadModal.vue';
import PreviewModal from '../components/PreviewModal.vue';

const MailEnums = {
  Status: {
    RECEIVED: 'Received',
    SCAN_REQUESTED: 'Scan_requested',
    SCANNED: 'Scanned',
    FORWARD_REQUESTED: 'Forward_requested',
    FORWARDED: 'Forwarded',
    SHRED_REQUESTED: 'Shred_requested',
    SHREDDED: 'Shredded'
  }
};

export default {
  name: 'RAMailRequest',
  components: {
    ScanUploadModal,
    PreviewModal
  },
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const mailItems = ref([]);
    const activeMainTab = ref('requested');
    const viewMode = ref('table');
    const searchQuery = ref('');
    const dateFilter = ref('');
    const showMobileFilters = ref(false);
    const debouncedSearch = ref(null);
    const showScanModal = ref(false);
    const selectedMailId = ref(null);
    const showPreviewModal = ref(false);
    const selectedMail = ref(null);

    // Only two main tabs
    const requestedStatuses = ['Scan_requested', 'Forward_requested', 'Shred_requested'];
    const completedStatuses = ['Scanned', 'Forwarded', 'Shredded'];

    const filteredMail = computed(() => {
      const statusList = activeMainTab.value === 'requested' ? requestedStatuses : completedStatuses;
      return mailItems.value.filter(mail => statusList.includes(mail.status));
    });

    const filteredAndSortedMail = computed(() => {
      let filtered = filteredMail.value;
      // Search
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(mail => 
          mail.business?.legalBusinessName?.toLowerCase().includes(query) ||
          mail.mailType.toLowerCase().includes(query) ||
          mail.description?.toLowerCase().includes(query)
        );
      }
      // Date filter
      if (dateFilter.value) {
        const now = new Date();
        filtered = filtered.filter(mail => {
          const mailDate = new Date(mail.receivedDate);
          switch (dateFilter.value) {
            case 'today':
              return mailDate.toDateString() === now.toDateString();
            case 'week': {
              const weekAgo = new Date(now.setDate(now.getDate() - 7));
              return mailDate >= weekAgo;
            }
            case 'month': {
              const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
              return mailDate >= monthAgo;
            }
            default:
              return true;
          }
        });
      }
      return filtered;
    });

    const getRequestedCount = computed(() => {
      return mailItems.value.filter(mail => requestedStatuses.includes(mail.status)).length;
    });
    const getCompletedCount = computed(() => {
      return mailItems.value.filter(mail => completedStatuses.includes(mail.status)).length;
    });

    const hasActiveFilters = computed(() => {
      return searchQuery.value || dateFilter.value;
    });

    const getMailCount = (status) => {
      return mailItems.value.filter(mail => mail.status === status).length;
    };

    const formatMailId = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}${month}${day}-${hours}${minutes}`;
    };

    const formatDate = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const statusLabel = (status) => {
      const statusMap = {
        Received: 'Received',
        Scan_requested: 'Scan Requested',
        Scanned: 'Scanned',
        Forward_requested: 'Forward Requested',
        Forwarded: 'Forwarded',
        Shred_requested: 'Shred Requested',
        Shredded: 'Shredded'
      };
      return statusMap[status] || status;
    };

    const handleSearch = () => {
      if (debouncedSearch.value) {
        clearTimeout(debouncedSearch.value);
      }
      debouncedSearch.value = setTimeout(() => {
        // Search logic is handled in computed property
      }, 300);
    };

    const handleFilter = () => {
      // Filter logic is handled in computed property
    };

    const clearFilters = () => {
      searchQuery.value = '';
      dateFilter.value = '';
    };

    const setMainTab = (tab) => {
      activeMainTab.value = tab;
    };

    const fetchMailRequests = async () => {
      loading.value = true;
      error.value = null;
      try {
        // Get all mail items
        const response = await incomingMailService.getIncomingMail();
        if (response?.data) {
          mailItems.value = response.data;
        } else {
          mailItems.value = [];
        }
      } catch (err) {
        console.error('Error fetching mail requests:', err);
        error.value = 'Failed to load mail requests';
      } finally {
        loading.value = false;
      }
    };

    const openScanUpload = (mailId) => {
      selectedMailId.value = mailId;
      showScanModal.value = true;
    };

    const closeScanModal = () => {
      showScanModal.value = false;
      selectedMailId.value = null;
    };

    const handleScanSuccess = async () => {
      await fetchMailRequests(); // Refresh the mail list
    };    const handleForward = async (mail) => {
      try {
        const forwardDetails = {
          businessId: mail.businessId,
          mailId: mail.id,
          status: MailEnums.Status.FORWARDED,
          forwardedTo: mail.forwardedTo || '',
          trackingNumber: mail.forwardedTrackingNumber || '',
          lastActionDate: new Date().toISOString()
        };
        
        const response = await incomingMailService.forwardMail(mail.id, forwardDetails);
        if (response.success) {
          mail.status = MailEnums.Status.FORWARDED;
          await fetchMailRequests(); // Refresh data from server
          if (activeMainTab.value === 'requested') {
            setMainTab('completed'); // Switch to completed tab
          }
        }
      } catch (error) {
        alert('Failed to forward mail.');
      }
    };    const handleShred = async (mail) => {
      try {
        const shredDetails = {
          businessId: mail.businessId,
          mailId: mail.id,
          status: MailEnums.Status.SHREDDED,
          reason: mail.shreddingReason || '',
          confirmation: mail.shreddingConfirmation || '',
          lastActionDate: new Date().toISOString()
        };
        
        const response = await incomingMailService.shredMail(mail.id, shredDetails);
        if (response.success) {
          mail.status = MailEnums.Status.SHREDDED;
          await fetchMailRequests(); // Refresh data from server
          if (activeMainTab.value === 'requested') {
            setMainTab('completed'); // Switch to completed tab
          }
        }
      } catch (error) {
        alert('Failed to shred mail.');
      }
    };

    const viewMailPreview = (mail) => {
      selectedMail.value = mail;
      showPreviewModal.value = true;
    };

    const closePreviewModal = () => {
      showPreviewModal.value = false;
      selectedMail.value = null;
    };

    const requestScan = (mail) => {
      this.scanMail(mail);
    };

    onMounted(() => {
      fetchMailRequests();
    });

    // Debug: log grid view data when grid is active
    watch([viewMode, filteredAndSortedMail], ([mode, mails]) => {
      if (mode === 'grid') {
        console.log('Grid view mails:', mails);
      }
    });

    return {
      loading,
      error,
      mailItems,
      activeMainTab,
      viewMode,
      searchQuery,
      dateFilter,
      filteredAndSortedMail,
      getRequestedCount,
      getCompletedCount,
      hasActiveFilters,
      getMailCount,
      formatMailId,
      formatDate,
      statusLabel,
      handleSearch,
      handleFilter,
      clearFilters,
      setMainTab,
      fetchMailRequests,
      showMobileFilters,
      openScanUpload,
      showScanModal,
      selectedMailId,
      closeScanModal,
      handleScanSuccess,
      showPreviewModal,
      selectedMail,
      viewMailPreview,
      closePreviewModal,
      handleForward,
      handleShred,
      requestScan
    };
  }
};
</script>

<style scoped>
.mail-request-container {
  padding: 2.0rem;
  margin: 0 auto;
  width: 100%;
}

.header-section {
  margin-bottom: 2rem;
}

h1 {
  margin-bottom: 1.5rem;
  color: #1f2937;
  font-size: 1.875rem;
}

.search-filter-bar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.search-box input {
  width: 30%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
}

.filter-group {
  display: flex;
  gap: 0.5rem;
}

.filter-group select {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  min-width: 150px;
}

.main-tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.main-tabs {
  display: flex;
  gap: 1rem;
}

.main-tab-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  transition: color 0.2s;
}

.main-tab-btn i {
  font-size: 1.25rem;
}

.main-tab-btn.active {
  color: #22c55e;
  border-bottom: 2px solid #22c55e;
  margin-bottom: -0.5rem;
}

.view-toggle {
  display: flex;
  gap: 0.5rem;
}

.view-toggle button {
  padding: 0.5rem 1.25rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.view-toggle button.active {
  background: #22c55e;
  color: white;
  border-color: #22c55e;
}

.mail-list, .mail-card {
  all: unset;
  display: block;
  background: #fff;
  color: #000;
}

.mail-card {
  background: white;
  border-radius: 0.5rem;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  flex: 1 1 300px;
  max-width: 350px;
  min-width: 250px;
  margin-bottom: 1rem;
}

.mail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.mail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.mail-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.mail-id {
  font-weight: 600;
  color: #1f2937;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-block;
}

.mail-date {
  color: #6b7280;
  font-size: 0.875rem;
}

.mail-body {
  margin-bottom: 1rem;
}

.mail-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.detail-row strong {
  color: #4b5563;
  min-width: 100px;
}

.mail-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
}

.mail-table th, .mail-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 250px; /* Maximum width for any cell */
}

.mail-table th {
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
}

.mail-table tr:last-child td {
  border-bottom: none;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  background: white;
  border-radius: 0.5rem;
  margin: 1rem 0;
}

.loading-state i,
.error-state i,
.empty-state i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.loading-state i {
  color: #22c55e;
}

.error-state i {
  color: #dc2626;
}

.empty-state i {
  color: #6b7280;
}

.retry-btn,
.clear-filters-btn {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: #22c55e;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
}

.retry-btn:hover,
.clear-filters-btn:hover {
  background: #16a34a;
}

.mobile-filter-drawer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 1rem 1rem 0 0;
  padding: 1.5rem;
  transform: translateY(100%);
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
  box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.1);
}

.mobile-filter-drawer.open {
  transform: translateY(0);
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.drawer-header h3 {
  margin: 0;
  color: #1f2937;
}

.drawer-header button {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-section label {
  color: #4b5563;
  font-weight: 500;
}

.filter-section select {
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.drawer-footer {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.drawer-footer button {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
}

.clear-btn {
  background: #f3f4f6;
  color: #4b5563;
}

.apply-btn {
  background: #22c55e;
  color: white;
}

@media (max-width: 768px) {
  .mail-request-container {
    padding: 1rem;
    margin: 0;
  }

  .search-filter-bar {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .filter-group {
    display: none;
  }

  .main-tabs-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .main-tabs {
    flex-wrap: wrap;
  }

  .view-toggle {
    justify-content: flex-end;
  }

  .mail-list {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .mail-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .mail-date {
    font-size: 0.75rem;
  }

  .mobile-filter-button {
    display: block;
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    background: #22c55e;
    color: white;
    padding: 0.75rem;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 100;
  }
}

@media (min-width: 769px) {
  .mobile-filter-button {
    display: none;
  }
}

.mail-grid-section {
  margin: 0;
  padding: 0;
}

.mail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 1.25rem;
}

.mail-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  padding: 1.25rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mail-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.mail-id {
  font-weight: 600;
  color: #374151;
  font-size: 1.05rem;
}

.mail-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.97rem;
  color: #222;
}

.mail-info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.mail-info-row strong {
  min-width: 100px;
}

.mail-card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-right: 0.5rem;
  margin-bottom: 0;
  padding: 0.35rem 0.75rem;
  font-size: 0.95em;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.action-btn.scan { background: #10b981; color: #fff; }
.action-btn.forward { background: #22c55e; color: #fff; }
.action-btn.shred { background: #ef4444; color: #fff; }
.action-btn.preview { background: #4b5563; color: #fff; }
.action-btn.preview:hover { background: #374151; }

.action-btn i { margin-right: 0.25em; }

@media (max-width: 768px) {
  .mail-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .mail-card {
    padding: 1rem 0.75rem;
  }
}
</style>